import React, { useState } from 'react';
import { FileText, Edit, Download, Brain } from 'lucide-react';
import { jsPDF } from 'jspdf';

import EssaySamples from './EssaySamples';
import TemplateWizard from './TemplateWizard';
import GeneratedEssay from './GeneratedEssay';
import KnowledgeGraph from './KnowledgeGraph';
import { essayTemplates, categories, templateTypes, usefulExpressions } from '../data/essayTemplates';
import { useUISettings } from './UISettingsContext';

const Essay = () => {
  const [activeSubTab, setActiveSubTab] = useState('samples');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [templateData, setTemplateData] = useState({
    type: 'informal',
    recipient: '',
    sender: '',
    date: '',
    subject: '',
    greeting: '',
    opening: '',
    mainBody: '',
    conclusion: '',
    closing: ''
  });

  // Filter samples based on search and category
  const filteredSamples = essayTemplates.filter(sample => {
    const matchesCategory = selectedCategory === 'all' || sample.category === selectedCategory;
    const matchesSearch = sample.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sample.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateChange = (field, value) => {
    setTemplateData(prev => ({ ...prev, [field]: value }));
  };

  // Autofill function to populate wizard with sample data
  const autofillFromSample = (sample) => {
    setTemplateData({
      type: sample.type,
      recipient: sample.content.recipient,
      sender: sample.content.sender,
      date: sample.content.date,
      subject: sample.content.subject,
      greeting: sample.content.greeting,
      opening: sample.content.opening,
      mainBody: sample.content.mainBody,
      conclusion: sample.content.conclusion,
      closing: sample.content.closing
    });
    setActiveSubTab('wizard');
  };

  // Enhanced PDF generation using jsPDF
  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Set up the document
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    let yPosition = 30;

    // Header
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(templateData.type.toUpperCase().replace('-', ' ') + ' LETTER/EMAIL', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 20;
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Letter information
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    
    const letterInfo = [
      `Date: ${templateData.date || '[Date]'}`,
      `To: ${templateData.recipient || '[Recipient]'}`,
      `From: ${templateData.sender || '[Sender]'}`,
      `Subject: ${templateData.subject || '[Subject]'}`
    ];

    letterInfo.forEach(info => {
      doc.text(info, margin, yPosition);
      yPosition += 8;
    });

    yPosition += 10;

    // Content sections with different colors/formatting
    const sections = [
      { label: 'Greeting:', content: templateData.greeting || '[Greeting]', color: [0, 100, 200] },
      { label: 'Opening:', content: templateData.opening || '[Opening paragraph]', color: [0, 150, 0] },
      { label: 'Main Body:', content: templateData.mainBody || '[Main body content]', color: [150, 0, 150] },
      { label: 'Conclusion:', content: templateData.conclusion || '[Concluding paragraph]', color: [200, 100, 0] },
      { label: 'Closing:', content: templateData.closing || '[Closing]', color: [200, 0, 0] }
    ];

    sections.forEach(section => {
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 30;
      }

      // Section label
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...section.color);
      doc.text(section.label, margin, yPosition);
      yPosition += 8;

      // Section content
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      
      const lines = doc.splitTextToSize(section.content, contentWidth);
      lines.forEach(line => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 30;
        }
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });
      
      yPosition += 8;
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated by EssayRight - Page ${i} of ${pageCount}`, pageWidth / 2, 285, { align: 'center' });
    }

    // Save the PDF
    const fileName = `${templateData.type}-letter-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const resetTemplate = () => {
    setTemplateData({
      type: 'informal',
      recipient: '',
      sender: '',
      date: '',
      subject: '',
      greeting: '',
      opening: '',
      mainBody: '',
      conclusion: '',
      closing: ''
    });
  };

  const { tabLabelMode } = useUISettings();

  const subTabs = [
    { id: 'samples', label: 'Essay Samples', icon: FileText },
    { id: 'wizard', label: 'Template Wizard', icon: Edit },
    { id: 'generated', label: 'Generated Essay', icon: Download },
    { id: 'knowledge-graph', label: 'Knowledge Graph', icon: Brain }
  ];

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {subTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeSubTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tabLabelMode === 'icon-text' && <span>{tab.label}</span>}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Essay Samples Sub-tab */}
        {activeSubTab === 'samples' && (
          <EssaySamples
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            filteredSamples={filteredSamples}
            autofillFromSample={autofillFromSample}
          />
        )}

        {/* Template Wizard Sub-tab */}
        {activeSubTab === 'wizard' && (
          <TemplateWizard
            templateData={templateData}
            handleTemplateChange={handleTemplateChange}
            templateTypes={templateTypes}
            usefulExpressions={usefulExpressions}
            resetTemplate={resetTemplate}
            setActiveTab={setActiveSubTab}
          />
        )}

        {/* Generated Essay Sub-tab */}
        {activeSubTab === 'generated' && (
          <GeneratedEssay
            templateData={templateData}
            generatePDF={generatePDF}
          />
        )}

        {/* Knowledge Graph Sub-tab */}
        {activeSubTab === 'knowledge-graph' && (
          <KnowledgeGraph />
        )}
      </div>
    </div>
  );
};

export default Essay; 